/**
 * Marks to review — AI marks the reviewing markers did not accept.
 *
 * Every dialogue answer is marked by one AI provider and reviewed by the
 * others. When a reviewer would give a different mark, or nobody could
 * review it, the answer lands here and the learner sees a provisional mark.
 * The mark entered here replaces it in the learner's results and progress.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import type { MarkReview } from "../../types";
import { MathText } from "../../components/Math";

const pct = (score: number) => Math.round(score * 100);

function ReviewCard({ review }: { review: MarkReview }) {
  const queryClient = useQueryClient();
  const [mark, setMark] = useState(String(pct(review.provisional_score)));
  const [image, setImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState("");

  const decide = useMutation({
    mutationFn: (score: number) => apiPost<MarkReview>(`/api/tutor/mark-reviews/${review.id}`, { score }),
    onSuccess: () => {
      // The decision changes the queue and the learner's standing on the roster.
      queryClient.invalidateQueries({ queryKey: ["mark-reviews"] });
      queryClient.invalidateQueries({ queryKey: ["tutor-roster"] });
      queryClient.invalidateQueries({ queryKey: ["tutor-dashboard"] });
    },
  });

  const value = Number(mark);
  const valid = mark.trim() !== "" && Number.isFinite(value) && value >= 0 && value <= 100;

  async function showImage() {
    try {
      const res = await apiGet<{ image: string | null }>(`/api/tutor/mark-reviews/${review.id}/answer-image`);
      if (res.image) setImage(res.image);
      else setImageError("The handwritten page is no longer stored.");
    } catch (err) {
      setImageError(err instanceof Error ? err.message : "Could not load the handwritten answer.");
    }
  }

  return (
    <article className="card mark-review">
      <header className="mark-review-head">
        <div>
          <strong>{review.learner_name}</strong>
          <span className="text-muted"> · {review.lesson_title}</span>
        </div>
        <span className="mark-review-why">
          {review.review_status === "unreviewed"
            ? "No other marker could review this mark"
            : "The markers gave different marks"}
        </span>
      </header>

      <div className="mark-review-question">
        <MathText content={review.question} />
      </div>

      <h3 className="mark-review-label">Learner's answer</h3>
      <div className="mark-review-answer">
        {review.answer ? <MathText content={review.answer} /> : <p className="text-muted">No typed answer.</p>}
        {review.working && (
          <p className="text-muted"><strong>Working:</strong> {review.working}</p>
        )}
        {review.handwritten && !image && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={showImage}>
            Show the handwritten answer
          </button>
        )}
        {image && <img className="mark-review-image" src={image} alt={`${review.learner_name}'s handwritten answer`} />}
        {imageError && <p className="text-muted">{imageError}</p>}
      </div>

      <h3 className="mark-review-label">How it was marked</h3>
      <ul className="mark-review-marks">
        <li>
          <strong>{review.marker}</strong> marked it {pct(review.marker_score)}%
          {review.marker_feedback && <span className="text-muted"> — {review.marker_feedback}</span>}
        </li>
        {review.reviews.map((r) => (
          <li key={r.provider}>
            <strong>{r.provider}</strong> would give {pct(r.score)}%{" "}
            <span className={r.accepts ? "mark-agree" : "mark-disagree"}>
              ({r.accepts ? "agrees" : "disagrees"})
            </span>
            {r.reason && <span className="text-muted"> — {r.reason}</span>}
          </li>
        ))}
      </ul>
      <p className="text-muted">
        The learner currently sees {pct(review.provisional_score)}% for this answer.
      </p>

      <form
        className="mark-review-decide"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) decide.mutate(value / 100);
        }}
      >
        <label>
          Your mark for this answer (%)
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={mark}
            onChange={(e) => setMark(e.target.value)}
            disabled={decide.isPending}
          />
        </label>
        <button type="submit" className="btn btn-primary" disabled={!valid || decide.isPending}>
          {decide.isPending ? "Saving…" : "Confirm mark"}
        </button>
      </form>
      {decide.isError && (
        <p className="error-message">{decide.error instanceof Error ? decide.error.message : "Could not save the mark."}</p>
      )}
    </article>
  );
}

export function MarkReviewsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useQuery({
    queryKey: ["mark-reviews"],
    queryFn: () => apiGet<MarkReview[]>("/api/tutor/mark-reviews"),
  });

  return (
    <div className="mark-reviews">
      <h1>Marks to review</h1>
      <p className="text-muted">
        Each written answer is marked by one AI marker and checked by the others. These are the answers
        they did not agree on, or that no other marker was available to check. The mark you confirm
        replaces the provisional one in the learner's results and progress.
      </p>
      {isLoading && <p className="text-muted">Loading…</p>}
      {data && data.length === 0 && (
        <p className="card text-muted">
          {isAdmin ? "No marks are waiting for a decision." : "No marks from your learners are waiting for you."}
        </p>
      )}
      {data?.map((review) => <ReviewCard key={review.id} review={review} />)}
    </div>
  );
}
